import { useCallback, useEffect, useState } from "react";
import { Check, ExternalLink, Loader2, MessageCircle, RefreshCw, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { apiError } from "../lib/auth";

const inputCls = "w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 transition-colors";

const Step = ({ n, done, title, children }) => (
  <div className="flex gap-4">
    <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
      done ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 text-slate-300"}`}>
      {done ? <Check className="w-3.5 h-3.5" /> : n}
    </span>
    <div className="flex-1 min-w-0 pb-5">
      <p className={`text-sm font-semibold ${done ? "text-slate-400" : "text-slate-100"}`}>{title}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  </div>
);

/* Telegram approvals in three steps: create the bot, paste its token, tap
   the link. The link carries a one-time code, so only the person holding it
   can tie a chat to this workspace - a bot is public, and anyone can message
   it. */
export const TelegramSetup = () => {
  const [st, setSt] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get("/telegram/status").then(({ data }) => setSt(data)).catch(() => setSt({ configured: false })), []);
  useEffect(() => { load(); }, [load]);

  // While waiting for the user to tap the link, watch for the chat to arrive.
  useEffect(() => {
    if (!st?.configured || st.chat_linked) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [st?.configured, st?.chat_linked, load]);

  const connect = async (e) => {
    e.preventDefault();
    setBusy("connect");
    try {
      await api.post("/telegram/connect", { bot_token: token });
      setToken("");
      toast.success("Bot connected - now tap the link to link your chat");
      load();
    } catch (err) { toast.error(apiError(err, "Could not connect the bot")); }
    finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try {
      await api.post("/telegram/test");
      toast.success("Test message sent - check Telegram");
    } catch (err) { toast.error(apiError(err, "The test message failed")); }
    finally { setBusy(""); }
  };

  const relink = async () => {
    setBusy("relink");
    try {
      const { data } = await api.post("/telegram/relink");
      setSt((s) => ({ ...s, link_url: data.link_url, chat_linked: false }));
      toast.success("New link ready - open it from the chat you want approvals in");
    } catch (err) { toast.error(apiError(err, "Could not create a new link")); }
    finally { setBusy(""); }
  };

  if (!st) return <div className="glass rounded-2xl p-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-300" /></div>;

  return (
    <div className="glass rounded-2xl p-6 space-y-5" data-testid="telegram-setup">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4" style={{ color: "#29A9EB" }} />
          <h3 className="font-heading font-bold text-slate-100">Telegram approvals</h3>
        </div>
        {st.chat_linked ? (
          <span className="text-xs font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-2.5 py-1">
            Live · @{st.bot_username} → {st.chat_title || "your chat"}
          </span>
        ) : st.configured ? (
          <span className="text-xs font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-full px-2.5 py-1">Waiting for your chat</span>
        ) : null}
      </div>

      <div>
        <Step n={1} done={st.configured} title="Create a bot with @BotFather">
          <p className="text-xs text-slate-400 leading-relaxed">
            In Telegram, open <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-indigo-300 hover:text-indigo-200">@BotFather</a>,
            send <code className="text-slate-200">/newbot</code>, pick a name (e.g. <em>Enpulsed Approvals</em>) and a username ending in
            <code className="text-slate-200"> bot</code>. It replies with a token like <code className="text-slate-200">123456:ABC-…</code>.
          </p>
        </Step>

        <Step n={2} done={st.configured} title={st.configured ? `Connected as @${st.bot_username}` : "Paste the token"}>
          <form onSubmit={connect} className="flex gap-2">
            <input value={token} onChange={(e) => setToken(e.target.value)} required={!st.configured}
              placeholder={st.configured ? "Paste a new token to change bots" : "123456:ABC-..."} className={inputCls} />
            <button disabled={busy === "connect" || !token}
              className="px-4 rounded-xl bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap">
              {busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin" /> : st.configured ? "Change bot" : "Connect"}
            </button>
          </form>
          {st.configured && !st.webhook_ok && (
            <p className="mt-2 text-xs text-amber-300 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Telegram isn't delivering to us yet{st.webhook_error ? `: ${st.webhook_error}` : ""}. Paste the token again to re-register.
            </p>
          )}
        </Step>

        <Step n={3} done={st.chat_linked} title="Link the chat that should get approvals">
          {st.configured ? (
            <div className="flex flex-wrap items-center gap-2">
              {!st.chat_linked && st.link_url && (
                <a href={st.link_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold" style={{ background: "#29A9EB" }}>
                  Open in Telegram & press Start <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              {st.chat_linked && (
                <button onClick={test} disabled={busy === "test"}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-semibold">
                  {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send a test
                </button>
              )}
              <button onClick={relink} disabled={busy === "relink"}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-700 text-slate-300 text-xs font-semibold hover:border-slate-600">
                <RefreshCw className="w-3.5 h-3.5" /> {st.chat_linked ? "Move to another chat" : "New link"}
              </button>
              <p className="w-full text-[11px] text-slate-500 mt-1">
                {st.chat_linked
                  ? "To use a group instead: add the bot to the group, press \"Move to another chat\", and open the link from that group."
                  : "The link works once and only for this workspace. This page updates by itself when it's done."}
              </p>
            </div>
          ) : (
            <p className="text-xs text-slate-500">Available once the bot is connected.</p>
          )}
        </Step>
      </div>
    </div>
  );
};
